import { useState } from 'react'
import { Alert, Button, Group, Loader, Progress, Stack, Text } from '@mantine/core'
import { IconCircleCheck, IconCircleDashed, IconCircleX, IconUsb } from '@tabler/icons-react'

import {
  baixarFirmware, baixarParticoes, registrarGravacao, useFirmwareDoRobo, type RoboFrota,
} from '@/api/robo'
import { abrirCarregador } from './esptool'
import {
  gravarFirmware, type EventoGravacao, type PassoGravacao, type ResultadoGravacao,
} from './gravador'
import { motivoDaLeituraPerdida, versaoDoChrome, vigiarLeitura } from './leituraPerdida'
import { abrirLinhas, pedirPorta, temWebSerial } from './serial'

const PASSOS: Array<{ passo: PassoGravacao; rotulo: string }> = [
  { passo: 'baixar', rotulo: 'Baixar e conferir o firmware' },
  { passo: 'identificar', rotulo: 'Ver o que o robô roda hoje' },
  { passo: 'conectar', rotulo: 'Entrar no modo de gravação e conferir a placa' },
  { passo: 'gravar', rotulo: 'Gravar e verificar' },
  { passo: 'reiniciar', rotulo: 'Reiniciar' },
  { passo: 'confirmar', rotulo: 'O robô informar a versão nova' },
]

type Estado = 'pendente' | 'andando' | 'feito' | 'falhou'

function Icone({ estado }: { estado: Estado }) {
  if (estado === 'feito') return <IconCircleCheck size={18} color="var(--mantine-color-teal-7)" />
  if (estado === 'falhou') return <IconCircleX size={18} color="var(--mantine-color-red-7)" />
  if (estado === 'andando') return <Loader size={16} />
  return <IconCircleDashed size={18} color="var(--mantine-color-gray-5)" />
}

async function sha256Hex(dados: Uint8Array): Promise<string> {
  const copia = new Uint8Array(dados)
  const h = await crypto.subtle.digest('SHA-256', copia)
  return Array.from(new Uint8Array(h), (b) => b.toString(16).padStart(2, '0')).join('')
}

/**
 * Grava pela USB a versão do catálogo para o modelo deste robô. Só o app e o
 * otadata são escritos: Wi-Fi e credencial ficam, e o robô volta configurado.
 */
export function GravarFirmware({ robo, aoConcluir }: { robo: RoboFrota; aoConcluir: () => void }) {
  const suporta = temWebSerial()
  const catalogo = useFirmwareDoRobo(robo.public_id, suporta)
  const [rodando, setRodando] = useState(false)
  const [estados, setEstados] = useState<Partial<Record<PassoGravacao, Estado>>>({})
  const [progresso, setProgresso] = useState<number | null>(null)
  const [resultado, setResultado] = useState<ResultadoGravacao | null>(null)
  const [trilhaFalhou, setTrilhaFalhou] = useState(false)

  if (!suporta) {
    return (
      <Text size="sm" c="dimmed">
        Gravar pela USB funciona no Chrome ou no Edge, no computador.
      </Text>
    )
  }
  if (catalogo.isLoading) return <Loader size="sm" />
  if (catalogo.isError) {
    return <Text size="sm" c="red.7">Não foi possível consultar o catálogo de firmware.</Text>
  }
  const { manifesto, motivo } = catalogo.data ?? { manifesto: null, motivo: null }
  if (!manifesto) return <Text size="sm" c="dimmed">{motivo}</Text>

  const atualizado = robo.firmware_version === manifesto.versao

  const iniciar = async () => {
    let porta
    try {
      porta = await pedirPorta()
    } catch {
      return
    }
    setRodando(true)
    setEstados({})
    setProgresso(null)
    setResultado(null)
    setTrilhaFalhou(false)
    let atual: PassoGravacao | null = null
    const aoAvancar = (e: EventoGravacao) => {
      if (e.tipo === 'progresso') {
        setProgresso(e.total > 0 ? Math.round((100 * e.feitos) / e.total) : 0)
        return
      }
      const anterior = atual
      atual = e.passo
      setEstados((s) => ({ ...s, ...(anterior ? { [anterior]: 'feito' } : {}), [e.passo]: 'andando' }))
    }
    // As duas etapas leem pela mesma porta vigiada: a que perde a leitura
    // primeiro é a que conta (TASK-11982).
    let perdida = false
    const vigiada = vigiarLeitura(porta, () => { perdida = true })
    let r: ResultadoGravacao
    try {
      r = await gravarFirmware({
        baixarApp: (versao) => baixarFirmware(robo.public_id, versao),
        baixarParticoes: (versao) => baixarParticoes(robo.public_id, versao),
        sha256: sha256Hex,
        abrirLinhas: () => abrirLinhas(vigiada),
        abrirCarregador: () => abrirCarregador(vigiada),
        motivoDaQueda: () => (perdida ? motivoDaLeituraPerdida(porta.connected, versaoDoChrome()) : null),
        aoAvancar,
      }, manifesto, { perfil: manifesto.perfil, mac: robo.mac ?? null })
    } catch {
      r = { ok: false, passo: atual ?? 'baixar', motivo: 'A gravação foi interrompida. Tente de novo.', mac: null }
    }
    setEstados((s) => ({ ...s, [r.ok ? 'confirmar' : r.passo]: r.ok ? 'feito' : 'falhou' }))
    setResultado(r)
    try {
      await registrarGravacao(robo.public_id, {
        versao: manifesto.versao, perfil: manifesto.perfil, mac: r.mac,
        resultado: r.ok ? 'ok' : 'falha', passo: r.ok ? null : r.passo,
      })
    } catch {
      setTrilhaFalhou(true)
    }
    setRodando(false)
    aoConcluir()
  }

  return (
    <Stack gap="sm">
      <Text size="sm">
        Versão do catálogo para este modelo: <Text span ff="monospace">{manifesto.versao}</Text>
        {robo.firmware_version && (
          <> · o robô informou <Text span ff="monospace">{robo.firmware_version}</Text></>
        )}
      </Text>
      <Text size="sm" c="dimmed">
        Só o firmware é trocado: a rede Wi-Fi e a credencial ficam no robô. Ele reinicia no fim.
      </Text>
      <Group>
        <Button leftSection={<IconUsb size={16} />} onClick={iniciar} loading={rodando}
          variant={atualizado ? 'default' : 'filled'}>
          {atualizado ? 'Gravar de novo pela USB' : 'Gravar pela USB'}
        </Button>
      </Group>

      {(rodando || resultado) && (
        <Stack gap={6} mt="xs">
          {PASSOS.map(({ passo, rotulo }) => (
            <Group key={passo} gap="xs" wrap="nowrap">
              <Icone estado={estados[passo] ?? 'pendente'} />
              <Text size="sm">{rotulo}</Text>
            </Group>
          ))}
          {estados.gravar === 'andando' && progresso !== null && (
            <Progress value={progresso} size="sm" aria-label="Progresso da gravação" />
          )}
        </Stack>
      )}

      {resultado && !resultado.ok && (
        <Alert color="red" title="Parou no meio">{resultado.motivo}</Alert>
      )}
      {resultado?.ok && (
        <Alert color="teal" title="Firmware gravado">
          O robô confirmou a versão {resultado.versao}.
        </Alert>
      )}
      {trilhaFalhou && (
        <Text size="xs" c="red.7">
          O console não registrou a trilha desta tentativa. Recarregue a página e confira a versão
          informada pelo robô.
        </Text>
      )}
    </Stack>
  )
}
